import React from 'react';
import { Link } from 'react-router-dom';
import SeoLandingLayout from '../../components/SeoLandingLayout/SeoLandingLayout';
import {
  IA_BRAND_NAME,
  IA_CASOS,
  IA_HUB,
  IA_SERVICES,
  buildIaBreadcrumb,
} from '../../config/iaBrand';
import './SolucionesIAPage.css';

const SERVICIOS_IA = [
  {
    ...IA_SERVICES.cumplimiento,
    resumen:
      'Artículo 4 del AI Act, alfabetización bonificable FUNDAE y documentación lista para la AESIA.',
  },
  {
    ...IA_SERVICES.agentes,
    resumen:
      'Automatización con agentes integrados en Business Central, Vulpin o CRM — para quien ya trabaja con nosotros.',
  },
  {
    ...IA_SERVICES.privada,
    resumen:
      'El dato nunca sale de tu servidor. On-premise o nube privada europea, integrada con Vulpin Social / Care.',
    destacado: true,
  },
  {
    ...IA_SERVICES.produccion,
    resumen:
      'Del prototipo (ChatGPT, Cursor, no-code) a producción: backend, API y Azure, con alguien que responde después del go-live.',
  },
];

const PRODUCTOS = [
  {
    id: 'transcriptor',
    nombre: 'E-ASY Transcriptor',
    categoria:
      'Motor de IA de Procesamiento de Lenguaje Natural (transcripción de voz a texto)',
    tecnologia: 'Procesamiento de Lenguaje Natural (NLP)',
    descripcion:
      'Transcribe dictados y sesiones habladas, reconoce terminología especializada del sector configurado, genera un resumen estructurado y lo vuelca automáticamente en el sistema de gestión conectado (ERP/historial).',
    coste: '10,00 €/usuaria/mes',
  },
  {
    id: 'ocr-vision',
    nombre: 'E-ASY OCR Vision',
    categoria:
      'Motor de IA de Visión Artificial (OCR y extracción de datos)',
    tecnologia:
      'Visión Artificial (Computer Vision) con Reconocimiento Óptico de Caracteres (OCR) y comprensión contextual',
    descripcion:
      'Extrae automáticamente metadatos (fechas, NIF, importes, nombres) de facturas en papel y formularios físicos, inyectando la información directamente en el sistema de gestión conectado (ERP).',
    coste:
      '10,00 €/usuaria estándar/mes + 40,00 €/año por licencia de administrador',
  },
];

const SolucionesIAPage = ({ darkMode, toggleDarkMode }) => (
  <SeoLandingLayout
    darkMode={darkMode}
    toggleDarkMode={toggleDarkMode}
    title="Inteligencia artificial para empresas | Cumplimiento, agentes y despliegue | E-ASY"
    description="Cuatro puertas: cumplir el AI Act, agentes sobre el ERP, IA privada on-premise o desplegar tu aplicación de IA en Azure. Servicio en remoto para toda España, con sede en Tenerife."
    keywords="desplegar IA empresas, inteligencia artificial para empresas, cumplimiento AI Act, agentes IA, IA on-premise, IA en producción Azure, E-ASY"
    canonical={IA_HUB.url}
    h1="Inteligencia artificial con E-ASY: cumplir, automatizar o desplegar"
    lede="Cuatro puertas para pymes, tercer sector y administración: cumplir el AI Act, agentes sobre lo que ya usáis, IA sin sacar el dato de vuestra red, o llevar a producción el prototipo que ya tenéis."
    ctaTo="/contacto"
    ctaLabel="Hablar de mi caso de IA"
    footerCtaLabel="Hablar de mi caso de IA"
    breadcrumbJsonLd={buildIaBreadcrumb([{ name: IA_HUB.label, url: IA_HUB.url }])}
    cobertura
  >
    <h2>Cuatro puertas, un mismo criterio</h2>
    <p>
      En {IA_BRAND_NAME} no vendemos “IA por moda”. Partimos de sistemas reales (Vulpin, Business Central,
      CRM), del RGPD y del uso diario. Elige la puerta de entrada que encaja:
    </p>

    <div className="ia-hub-services">
      {SERVICIOS_IA.map((servicio) => (
        <article
          key={servicio.id}
          className={`ia-hub-card${servicio.destacado ? ' ia-hub-card--featured' : ''}`}
        >
          <h3>{servicio.label}</h3>
          <p>{servicio.resumen}</p>
          <Link to={servicio.path} className="seo-landing-cta">
            Ver {servicio.shortLabel}
          </Link>
        </article>
      ))}
    </div>

    <h2>Prueba social y casos</h2>
    <p>
      Trabajamos con entidades del sector social donde el dato sensible es el núcleo del servicio.
      Consulta <Link to={IA_CASOS.path}>ACUFADE, AFFA y MPDL</Link> — y cómo encajan con{' '}
      <Link to={IA_SERVICES.privada.path}>IA privada</Link>.
    </p>

    <h2>Productos de IA en catálogo</h2>
    <p>
      Además de las cuatro puertas anteriores, comercializamos motores de IA con coste de suscripción
      transparente:
    </p>
    <div className="soluciones-ia-grid ia-hub-productos">
      {PRODUCTOS.map((producto) => (
        <article
          key={producto.id}
          className="soluciones-ia-card"
          aria-labelledby={`${producto.id}-titulo`}
        >
          <header className="soluciones-ia-card-header">
            <h3 id={`${producto.id}-titulo`} className="soluciones-ia-card-title">
              {producto.nombre}
            </h3>
          </header>
          <dl className="soluciones-ia-datos">
            <div className="soluciones-ia-fila">
              <dt>Categoría</dt>
              <dd>{producto.categoria}</dd>
            </div>
            <div className="soluciones-ia-fila soluciones-ia-fila--tecnologia">
              <dt>Tecnología de IA</dt>
              <dd>{producto.tecnologia}</dd>
            </div>
            <div className="soluciones-ia-fila soluciones-ia-fila--texto">
              <dd>
                <p>{producto.descripcion}</p>
              </dd>
            </div>
            <div className="soluciones-ia-fila soluciones-ia-fila--precio">
              <dt>Coste de suscripción</dt>
              <dd>{producto.coste}</dd>
            </div>
          </dl>
        </article>
      ))}
    </div>

    <h2>Blog relacionado</h2>
    <ul>
      <li>
        <Link to="/blog/de-prototipo-chatgpt-cursor-a-produccion">
          De prototipo ChatGPT o Cursor a producción: qué hay que montar
        </Link>
      </li>
      <li>
        <Link to="/blog/donde-desplegar-agente-ia-azure-on-premise">
          Dónde desplegar un agente de IA: Azure, on-premise o API pública
        </Link>
      </li>
      <li>
        <Link to="/blog/articulo-4-ai-act-pyme-canaria">
          Qué exige realmente el Artículo 4 del AI Act a una pyme canaria
        </Link>
      </li>
      <li>
        <Link to="/blog/ayudas-ia-kit-consulting-ris3-cdti">
          Kit Consulting, RIS3 y CDTI: qué ayuda encaja con tu proyecto de IA
        </Link>
        {' '}— si el bono es de digitalización cloud (Microsoft 365, Azure, Workspace),
        la continuidad está en{' '}
        <Link to="/servicios/consultoria-microsoft-azure">
          consultoría Microsoft, Azure y partners
        </Link>
        .
      </li>
    </ul>
  </SeoLandingLayout>
);

export default SolucionesIAPage;
